/**
 * Publisher-weighted forward forecast — pure utility module.
 *
 * Given the articles currently visible on a stock detail chart and the
 * per-publisher metrics map already fetched by `usePublisherMetrics`, this
 * computes a 10-point projected price line (one per `PriceTimeframe`) anchored
 * at the most recent article's `(publishedAt, priceAtPublish)`.
 *
 * The formula is spelled out in
 * `docs/superpowers/specs/2026-05-27-publisher-weighted-forecast-design.md`
 * and implemented here verbatim — see the "Formula" section.
 *
 * No React / no DOM / no I/O — kept side-effect-free so it's trivially
 * unit-testable.
 */
import { PublisherMetrics, StockArticleDto } from '../../api';

// ─── Public types ────────────────────────────────────────────────────────────

export interface ForecastPoint {
    /** Human-readable label, e.g. "1h", "1d", … "6m". */
    label: string;
    /** Milliseconds offset from `anchorTime` at which to plot this point. */
    offsetMs: number;
    /** Expected % move at this horizon (signed). */
    expectedPct: number;
    /** Floored weighted-stddev of contributions, in % (always ≥ sigmaFloorPct). */
    sigmaPct: number;
    /** `expectedPct + sigmaPct`, for the upper-band line. */
    upperPct: number;
    /** `expectedPct − sigmaPct`, for the lower-band line. */
    lowerPct: number;
    /** Anchor price scaled by `(1 + expectedPct/100)`. */
    projectedPrice: number;
    /** Anchor price scaled by `(1 + upperPct/100)`. */
    upperPrice: number;
    /** Anchor price scaled by `(1 + lowerPct/100)`. */
    lowerPrice: number;
    /** Number of articles whose weight contributed (> 0) to this horizon. */
    contributingAnalysts: number;
}

export interface ForecastResult {
    /** ISO instant of the anchor (most recent article's `publishedAt`). */
    anchorTime: string;
    /** Price the projection departs from (most recent article's `priceAtPublish`). */
    anchorPrice: number;
    /** Always length 10, in TIMEFRAMES order. */
    points: ForecastPoint[];
    /** True iff any horizon produced Σ weight > 0. */
    hasSignal: boolean;
    /** Why `hasSignal=false`. Absent when `hasSignal=true`. */
    reason?: string;
}

// ─── Timeframes (fixed order, must match `PriceTimeframe` on the backend) ────

export const TIMEFRAMES: ReadonlyArray<{ label: string; offsetMs: number }> = [
    { label: '1h', offsetMs: 3.6e6 },
    { label: '1d', offsetMs: 8.64e7 },
    { label: '1w', offsetMs: 6.048e8 },
    { label: '2w', offsetMs: 1.2096e9 },
    { label: '4w', offsetMs: 2.4192e9 },
    { label: '2m', offsetMs: 5.184e9 },
    { label: '3m', offsetMs: 7.776e9 },
    { label: '4m', offsetMs: 1.0368e10 },
    { label: '5m', offsetMs: 1.296e10 },
    { label: '6m', offsetMs: 1.5552e10 },
];

/** Field names on `PublisherMetrics` for each timeframe, in the same order as `TIMEFRAMES`. */
const SCORE_KEYS: Array<keyof PublisherMetrics> = [
    'score1h', 'score1d', 'score1w', 'score2w', 'score4w',
    'score2m', 'score3m', 'score4m', 'score5m', 'score6m',
];
const COUNT_KEYS: Array<keyof PublisherMetrics> = [
    'sampleCount1h', 'sampleCount1d', 'sampleCount1w', 'sampleCount2w', 'sampleCount4w',
    'sampleCount2m', 'sampleCount3m', 'sampleCount4m', 'sampleCount5m', 'sampleCount6m',
];

// ─── Options ─────────────────────────────────────────────────────────────────

export interface ComputeForecastOptions {
    /** Inject `Date.now()` for tests. */
    nowMs?: number;
    /** Age half-life in days. Default 14. */
    halfLifeDays?: number;
    /** Weight floor for n<2 analysts. Default 0.25. */
    lowSampleFloor?: number;
    /** Minimum band half-width in %. Default 0.25. */
    sigmaFloorPct?: number;
    /** Fallback anchor price when the most-recent article lacks `priceAtPublish`. */
    anchorOhlcClose?: number;
    /**
     * Explicit anchor `(price, timeMs)`. When present, the function uses this
     * directly and skips the latest-article-based anchor resolution. This is
     * how the stock detail page anchors the forecast at the live Polygon spot
     * rather than at the latest article's `priceAtPublish`, which can lag the
     * tape by hours or days.
     *
     * If both `anchor` and `anchorOhlcClose` are supplied, `anchor` wins.
     */
    anchor?: { price: number; timeMs: number };
}

// ─── Implementation ──────────────────────────────────────────────────────────

interface ArticleContribution {
    expectedPct: number;   // expected_a_T  = score_T × (stance / 100)
    weight: number;        // weight_a_T    = (conviction/100) × n_factor × age_decay
}

/**
 * Resolve the anchor `(timeMs, price, isoTime)` for the projection.
 *
 * Resolution order:
 *   1. `options.anchor` — explicit live-spot anchor (used by the stock detail
 *      page so the projection departs from the current tape rather than the
 *      latest article's stale price).
 *   2. The most recent article's `(publishedAt, priceAtPublish)`. Articles are
 *      passed in DESCENDING publishedAt order (per
 *      `StockPricesService.getStockArticles`), so `articles[0]` is most recent.
 *   3. The `options.anchorOhlcClose` fallback when the latest article lacks a
 *      price.
 */
function pickAnchor(
    articles: StockArticleDto[],
    options: ComputeForecastOptions,
): { anchorTime: string; anchorMs: number; anchorPrice: number } | null {
    // 1. Explicit live-spot anchor wins outright.
    if (options.anchor
        && typeof options.anchor.price === 'number' && Number.isFinite(options.anchor.price)
        && typeof options.anchor.timeMs === 'number' && Number.isFinite(options.anchor.timeMs)) {
        return {
            anchorTime:  new Date(options.anchor.timeMs).toISOString(),
            anchorMs:    options.anchor.timeMs,
            anchorPrice: options.anchor.price,
        };
    }

    // 2 + 3. Fall back to the latest article (+ optional OHLC-close override).
    const first = articles[0];
    if (!first || !first.publishedAt) return null;
    const anchorMs = Date.parse(first.publishedAt);
    if (!Number.isFinite(anchorMs)) return null;
    const anchorPrice = (typeof first.priceAtPublish === 'number' && Number.isFinite(first.priceAtPublish))
        ? first.priceAtPublish
        : (typeof options.anchorOhlcClose === 'number' && Number.isFinite(options.anchorOhlcClose))
            ? options.anchorOhlcClose
            : NaN;
    if (!Number.isFinite(anchorPrice)) return null;
    return { anchorTime: first.publishedAt, anchorMs, anchorPrice };
}

/**
 * Compute one article's contribution at one horizon. Returns `null` if the
 * article should be skipped at this horizon (no metrics, zero samples, etc.).
 */
function articleContribution(
    article: StockArticleDto,
    metrics: PublisherMetrics | undefined,
    timeframeIdx: number,
    nowMs: number,
    halfLifeDays: number,
    lowSampleFloor: number,
): ArticleContribution | null {
    if (!metrics) return null;

    const score = metrics[SCORE_KEYS[timeframeIdx]] as number | undefined;
    const sampleCount = metrics[COUNT_KEYS[timeframeIdx]] as number | undefined;
    if (score == null || !Number.isFinite(score)) return null;
    if (sampleCount == null || sampleCount <= 0) return null;

    const stance = Number(article.stance ?? 0);
    if (!Number.isFinite(stance) || stance === 0) return null;

    const conviction = Number(article.conviction ?? 0);
    if (!Number.isFinite(conviction) || conviction <= 0) return null;

    if (!article.publishedAt) return null;
    const publishedMs = Date.parse(article.publishedAt);
    if (!Number.isFinite(publishedMs)) return null;

    // expected_a_T = score_T × (stance / 100)
    const expectedPct = score * (stance / 100);

    // age_decay = 0.5 ^ (age_days / halfLifeDays)
    const ageDays = Math.max(0, (nowMs - publishedMs) / 86_400_000);
    const ageDecay = Math.pow(0.5, ageDays / halfLifeDays);

    // n_factor = (n < 2) ? lowSampleFloor : sqrt(n)
    const nFactor = sampleCount < 2 ? lowSampleFloor : Math.sqrt(sampleCount);

    const weight = (conviction / 100) * nFactor * ageDecay;
    if (!Number.isFinite(weight) || weight <= 0) return null;

    return { expectedPct, weight };
}

/**
 * Publisher-weighted forecast — see module-level JSDoc and the design spec.
 */
export function computeForecast(
    articles: StockArticleDto[],
    metricsByAuthor: Record<string, PublisherMetrics>,
    options: ComputeForecastOptions = {},
): ForecastResult {
    const nowMs          = options.nowMs          ?? Date.now();
    const halfLifeDays   = options.halfLifeDays   ?? 14;
    const lowSampleFloor = options.lowSampleFloor ?? 0.25;
    const sigmaFloorPct  = options.sigmaFloorPct  ?? 0.25;

    // Edge case: no articles at all.
    if (!articles || articles.length === 0) {
        return {
            anchorTime:  new Date(nowMs).toISOString(),
            anchorPrice: 0,
            points:      [],
            hasSignal:   false,
            reason:      'no articles in window',
        };
    }

    // Anchor: most-recent article. If we can't anchor (no priceAtPublish and
    // no fallback), we can't render a forecast at all.
    const anchor = pickAnchor(articles, options);
    if (!anchor) {
        return {
            anchorTime:  new Date(nowMs).toISOString(),
            anchorPrice: 0,
            points:      [],
            hasSignal:   false,
            reason:      'no anchor (latest article missing priceAtPublish)',
        };
    }

    // Pre-filter: only articles with a usable authorId & non-zero stance.
    const usable = articles.filter(a =>
        !!a.authorId &&
        Number.isFinite(Number(a.stance ?? 0)) &&
        Number(a.stance ?? 0) !== 0,
    );

    // Detect "no scored publisher anywhere" early so we can give a clean reason.
    const hasAnyScoredPublisher = usable.some(a => {
        const m = a.authorId ? metricsByAuthor[a.authorId] : undefined;
        if (!m) return false;
        return COUNT_KEYS.some(k => {
            const n = m[k] as number | undefined;
            return typeof n === 'number' && n > 0;
        });
    });

    const points: ForecastPoint[] = [];
    let anyHorizonHadSignal = false;

    for (let i = 0; i < TIMEFRAMES.length; i++) {
        const tf = TIMEFRAMES[i];

        const contributions: ArticleContribution[] = [];
        for (const a of usable) {
            const m = a.authorId ? metricsByAuthor[a.authorId] : undefined;
            const c = articleContribution(a, m, i, nowMs, halfLifeDays, lowSampleFloor);
            if (c) contributions.push(c);
        }

        const totalWeight = contributions.reduce((s, c) => s + c.weight, 0);

        let expectedPct = 0;
        let variance    = 0;
        if (totalWeight > 0) {
            anyHorizonHadSignal = true;
            expectedPct = contributions.reduce((s, c) => s + c.expectedPct * c.weight, 0) / totalWeight;
            variance    = contributions.reduce((s, c) => {
                const d = c.expectedPct - expectedPct;
                return s + d * d * c.weight;
            }, 0) / totalWeight;
        }

        const sigmaRaw = Math.sqrt(Math.max(0, variance));
        const sigmaPct = Math.max(sigmaRaw, sigmaFloorPct);
        const upperPct = expectedPct + sigmaPct;
        const lowerPct = expectedPct - sigmaPct;

        points.push({
            label:                tf.label,
            offsetMs:             tf.offsetMs,
            expectedPct,
            sigmaPct,
            upperPct,
            lowerPct,
            projectedPrice:       anchor.anchorPrice * (1 + expectedPct / 100),
            upperPrice:           anchor.anchorPrice * (1 + upperPct   / 100),
            lowerPrice:           anchor.anchorPrice * (1 + lowerPct   / 100),
            contributingAnalysts: contributions.length,
        });
    }

    if (!hasAnyScoredPublisher) {
        return {
            anchorTime:  anchor.anchorTime,
            anchorPrice: anchor.anchorPrice,
            points,
            hasSignal:   false,
            reason:      'no scored publishers',
        };
    }

    return {
        anchorTime:  anchor.anchorTime,
        anchorPrice: anchor.anchorPrice,
        points,
        hasSignal:   anyHorizonHadSignal,
        ...(anyHorizonHadSignal ? {} : { reason: 'no net signal across any horizon' }),
    };
}
